import { createContext, useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import invariant from 'tiny-invariant';

import { isServer } from 'src/shared/helpers/environment';
import { getRoute } from 'src/shared/helpers/url';
import type { RouteDescriptor } from 'src/shared/route';
import type { Page, PageViewed } from 'src/shared/telemetry';

type CurrentPage = Page | null;

type SegmentHistoryContext = {
  page: CurrentPage;
  setPage: ((page: CurrentPage) => void) | null;
  metadata: PageViewed;
  setMetadata: (metadata: PageViewed) => void;
};

const defaultValue: SegmentHistoryContext = {
  page: null,
  setPage: null,
  metadata: {},
  setMetadata: () =>
    invariant(false, 'Setting metadata before provider is ready'),
};

const context = createContext(defaultValue);

const hasRequiredMetadata = (route: RouteDescriptor, metadata?: PageViewed) => {
  if (!route.metadata) {
    return true;
  }

  if (!metadata) {
    return false;
  }

  const propertyKeys = Object.keys(metadata);
  return route.metadata.every(key => propertyKeys.includes(key));
};

const trackPage = (route: RouteDescriptor, metadata?: PageViewed) => {
  if (
    isServer() ||
    !route.page ||
    !window.analytics ||
    !hasRequiredMetadata(route, metadata)
  )
    return;

  analytics.page(route.page, metadata);
};

const useSegmentHistory = () => {
  const { setPage, metadata } = useContext(context);

  const history = useHistory();

  const setAndTrackPage = useCallback(
    (pathname: string) => {
      const route = getRoute(pathname);
      if (!route) return;

      setPage?.(route.page ?? null);
      trackPage(route, metadata);
    },
    [setPage, metadata],
  );

  useEffect(() => {
    const unlisten = history.listen(location =>
      setAndTrackPage(location.pathname),
    );

    return () => unlisten();
  }, [history, setAndTrackPage, metadata]);

  // only need to do this once per app instance
  useEffect(() => {
    const route = getRoute(history.location.pathname);
    if (route) trackPage(route, metadata);
  }, [metadata, history.location.pathname]);
};

const useTrackingMetadata = () => {
  const { metadata, setMetadata } = useContext(context);

  const mergeMetadata = (data: Partial<PageViewed>) => {
    setMetadata({ ...metadata, ...data });
  };

  return { metadata, mergeMetadata };
};

const { Provider } = context;

export {
  CurrentPage,
  Provider,
  SegmentHistoryContext,
  context,
  defaultValue,
  useSegmentHistory,
  useTrackingMetadata,
  hasRequiredMetadata,
};
